import React, { useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { authApi, getPublicKey } from "../api/api";
import OTPModal from "../components/OtpModal";
import ErrorModal from "../components/ErrorModal";
import SpinnerOverlay from "../components/SpinnerOverlay";
import FallbackPaymentModal from "../components/FallbackPaymentModal";
import razorpayUtils from "../utils/razorpayUtils";
import { saveCheckoutSession } from "../checkout/useCheckout";
import CriticalErrorHandler from "../utils/criticalErrorHandler";
import criticalLogger from "../utils/criticalLogger";

const {
  openRazorpayPayment,
  generateFallbackPaymentInstructions,
  detectInAppBrowser,
  getBrowserCompatibilityMessage,
} = razorpayUtils;

/**
 * Common payment page — shared by LandingPage.jsx (astrology) and NumerologyLandingPage.jsx.
 * Owns: wallet-balance check -> OTP-or-gateway decision -> gateway routing -> status polling
 * -> error handling -> success redirect. Takes the already-created order via route state.
 *
 * Route state contract: { orderId, userId, amount, leadId, customerName, customerContact,
 *   product: { id, service_name }, checkoutMode: 'standard' | 'custom' }
 *
 * NOTE on fidelity: this is a near-verbatim move of LandingPage.jsx's handlePayment (1359-2192)
 * and OTP orchestration (1087-1358, otpModal state). The one thing NOT moved is a confirmed-dead
 * `options` block (~254 lines) that built a Razorpay config with its own embedded handler but
 * was never referenced anywhere — openRazorpayPayment() builds its own config internally
 * (verified in razorpayUtils.js:63-81). See the plan file for how this was confirmed.
 *
 * Also NOT replicated: `setExistingOrder(null)`/`setExistingLead(null)` resets that appeared
 * alongside `setRechargeResState(null)` in the original success/failure paths — those reset
 * LandingPage.jsx's own return-visitor-dedup state, which doesn't exist on this page (the order
 * already exists by the time we're here). `rechargeResState` itself IS kept, as local state,
 * since handlePayment's own reuse-if-unchanged logic reads it.
 */
export default function CommonPaymentPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { orderId, userId, amount, leadId, customerName, customerContact, product, checkoutMode } = location.state || {};

  const [paymentLoading, setPaymentLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Preparing payment...");
  const [rechargeResState, setRechargeResState] = useState(null);
  const [errorModal, setErrorModal] = useState({ show: false, message: "", title: "" });
  const [browserWarning, setBrowserWarning] = useState(null);
  const [fallbackModal, setFallbackModal] = useState({
    show: false, amount: 0, orderId: null, instructions: null, triggerReason: null, errorInfo: null,
  });
  const [otpModal, setOtpModal] = useState({ show: false, orderId: null, userId: null, leadId: null, infoMessage: "" });
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const paymentIntervalRef = useRef(null);
  const paymentTimeoutRef = useRef(null);

  // --- verbatim from LandingPage.jsx:1087-1105 ---
  const sendOTP = async (mobile) => {
    try {
      const response = await authApi.post("/user/send-otp", { mobile });
      return response.data;
    } catch (err) {
      throw err;
    }
  };

  const verifyOTP = async (mobile, otp) => {
    try {
      const response = await authApi.post("/user/verify-otp", { mobile, otp });
      return response.data;
    } catch (err) {
      throw err;
    }
  };

  // --- verbatim from LandingPage.jsx:1107-1121 ---
  const startResendTimer = () => {
    setResendDisabled(true);
    setResendTimer(30);
    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          setResendDisabled(false);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // --- adapted from LandingPage.jsx:611-635 (fetchBalance) — same logic, no wallet-display state ---
  const fetchBalance = async (uid) => {
    try {
      const response = await api.get(`/wallet/balance/${uid}`);
      if (response.status === 200 && response.data.wallet_balance !== undefined) {
        return response.data.wallet_balance;
      }
      console.warn("Unexpected API response:", response);
      return null;
    } catch (err) {
      console.error("Failed to fetch wallet balance:", err);
      CriticalErrorHandler.reportApiError(`/wallet/balance/${uid}`, err, { user_id: uid, operation: "fetch_balance" });
      return null;
    }
  };

  // --- verbatim from LandingPage.jsx:1123-1311 (handleOTPVerify), minus the
  // setExistingOrder/setExistingLead resets (landing-page-only state, see note above) ---
  const handleOTPVerify = async (otp) => {
    const { orderId: otpOrderId, userId: otpUserId, leadId: otpLeadId } = otpModal;
    let otpVerificationSuccessful = false;

    try {
      if (!otp || otp.length !== 6) throw new Error("OTP must be exactly 6 digits");
      if (!/^\d{6}$/.test(otp)) throw new Error("OTP must contain only numbers");
      if (["000000", "111111", "123456", "999999", "888888", "777777"].includes(otp)) {
        throw new Error("Please enter a valid OTP");
      }

      const otpVerifyResult = await verifyOTP(customerContact, otp);
      if (!otpVerifyResult || otpVerifyResult.status !== "success") {
        throw new Error(otpVerifyResult?.error || otpVerifyResult?.message || "OTP verification failed");
      }
      otpVerificationSuccessful = true;

      CriticalErrorHandler.reportPaymentSuccess({
        message: "OTP verification successful for wallet payment",
        operation: "otp_verify_wallet_payment",
        mobile: customerContact, order_id: otpOrderId, user_id: otpUserId, lead_id: otpLeadId,
      });

      setOtpModal({ show: false, orderId: null, userId: null, leadId: null, infoMessage: "" });
      setPaymentLoading(true);

      const processRes = await api.post(`/orders/complete/${otpOrderId}`, {});

      if (processRes.data.status === "success") {
        try {
          await api.post(`/leads/converted/${otpLeadId}`);
        } catch (leadErr) {
          CriticalErrorHandler.reportApiError(`/leads/converted/${otpLeadId}`, leadErr, {
            lead_id: otpLeadId, order_id: otpOrderId, operation: "mark_lead_converted",
          });
        }
        setRechargeResState(null);
        const formattedOrderId = String(otpOrderId).padStart(6, "0");
        setBrowserWarning(null);
        navigate(`/order-success/ORD_${formattedOrderId}`);
      } else {
        CriticalErrorHandler.reportPaymentError(new Error("Order processing failed after OTP verification"), {
          operation: "order_completion_failed", order_id: otpOrderId, user_id: otpUserId, lead_id: otpLeadId,
          api_response: processRes.data,
        });
        setErrorModal({
          show: true,
          message: "Order processing failed: " + (processRes.data.message || "Unknown error"),
          title: "Processing Error",
        });
      }
    } catch (err) {
      if (!otpVerificationSuccessful) {
        const isOtpError =
          err.response?.status === 400 ||
          err.response?.data?.status === "failed" ||
          err.message?.toLowerCase().includes("otp") ||
          err.message?.toLowerCase().includes("digit") ||
          err.message?.toLowerCase().includes("number") ||
          err.message?.toLowerCase().includes("valid") ||
          err.response?.data?.message?.toLowerCase().includes("otp") ||
          err.response?.data?.error?.toLowerCase().includes("otp") ||
          err.response?.data?.error?.toLowerCase().includes("invalid") ||
          err.response?.data?.error?.toLowerCase().includes("expired");

        if (isOtpError) {
          let errorMessage = "Invalid OTP. Please try again.";
          if (err.response?.data?.error) errorMessage = err.response.data.error;
          else if (err.response?.data?.message) errorMessage = err.response.data.message;
          else if (err.message) errorMessage = err.message;

          CriticalErrorHandler.reportAuthError(err, {
            operation: "otp_verification_failed_wallet_payment", order_id: otpOrderId, user_id: otpUserId,
            lead_id: otpLeadId, mobile: customerContact, error_message: errorMessage, error_type: "otp_validation_failed",
          });
          throw new Error(errorMessage); // keeps modal open for retry, same as original
        }
      }

      setOtpModal({ show: false, orderId: null, userId: null, leadId: null, infoMessage: "" });
      CriticalErrorHandler.reportPaymentError(err, {
        operation: "order_completion_failed_after_otp", order_id: otpOrderId, user_id: otpUserId, lead_id: otpLeadId,
        mobile: customerContact, error_message: err.response?.data?.message || err.message,
        otp_verification_successful: otpVerificationSuccessful,
      });
      setErrorModal({
        show: true,
        message: "Order processing failed after OTP verification. Please contact support with your order details.",
        title: "Processing Error",
      });
    } finally {
      setPaymentLoading(false);
    }
  };

  // --- verbatim from LandingPage.jsx:1313-1346 ---
  const handleOTPResend = async () => {
    try {
      await sendOTP(customerContact);
      startResendTimer();
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || "";
      const isRecentlySentError = errorMessage && ["OTP recently sent", "recently sent", "cooldown", "wait"].some((s) => errorMessage.includes(s));
      if (isRecentlySentError) {
        startResendTimer();
      } else {
        CriticalErrorHandler.reportAuthError(err, { operation: "otp_resend", mobile: customerContact, error_message: errorMessage });
        setErrorModal({ show: true, message: `Resend failed: ${errorMessage || "Please try again."}`, title: "Resend Failed" });
      }
    }
  };

  // Confirm before actually cancelling — this page has no content of its own once the OTP
  // modal closes (it's just modals over a blank background), so leaving without confirming
  // used to strand the user on an empty screen with no way forward.
  const handleOTPClose = () => {
    if (!window.confirm("Are you sure you want to cancel? Your order will not be completed.")) {
      return;
    }
    setOtpModal({ show: false, orderId: null, userId: null, leadId: null, infoMessage: "" });
    setPaymentLoading(false);
    navigate("/", { replace: true });
  };

  // --- adapted from LandingPage.jsx:1359-2192 (handlePayment) — same logic, product/formData
  // references replaced with route-state values; dead `options` block (confirmed, see plan) dropped ---
  const handlePayment = async () => {
    if (!product) return;
    setPaymentLoading(true);

    const balanceValue = await fetchBalance(userId);
    if (criticalLogger.isHealthy()) {
      CriticalErrorHandler.reportPaymentGateway("Wallet balance fetched", { user_id: userId, wallet_balance: balanceValue, operation: "wallet_balance_check" });
    }

    let amountToBePaid = 0;
    if (balanceValue !== null && amount !== null) {
      if (parseFloat(balanceValue) < parseFloat(amount)) {
        amountToBePaid = parseFloat(amount) - parseFloat(balanceValue);
      }
      CriticalErrorHandler.reportPaymentGateway("Payment amount calculated", {
        user_id: userId, order_amount: amount, wallet_balance: balanceValue, amount_to_be_paid: amountToBePaid,
        operation: "payment_amount_calculation",
      });
    } else {
      amountToBePaid = parseFloat(amount) || 0;
      CriticalErrorHandler.reportPaymentGateway("Wallet balance unavailable - full payment required", {
        user_id: userId, order_amount: amount, amount_to_be_paid: amountToBePaid, operation: "wallet_balance_failure_fallback",
      });
    }

    if (amountToBePaid > 0) {
      let rechargeRes = rechargeResState;
      const existingAmount = rechargeRes?.data?.order?.amount;
      const shouldCreateNew = !rechargeRes || existingAmount !== amountToBePaid * 100;

      if (shouldCreateNew) {
        try {
          rechargeRes = await api.post("/wallet/initiate", { userId, amount: amountToBePaid, orderId, notes: { leadId } });
          setRechargeResState(rechargeRes);
        } catch (err) {
          setPaymentLoading(false);
          CriticalErrorHandler.reportPaymentError(err, { operation: "wallet_initiate", user_id: userId, amount: amountToBePaid, order_id: orderId });
          setErrorModal({ show: true, message: "Failed to initiate payment. Please try again.", title: "Payment Error" });
          return;
        }
      } else {
        CriticalErrorHandler.reportPaymentGateway("Reusing existing payment session", { user_id: userId, order_id: orderId, operation: "payment_session_reuse" });
      }

      const { order } = rechargeRes.data;
      setPaymentLoading(false);

      let razorpayKey;
      try {
        razorpayKey = await getPublicKey("RAZORPAY_KEY_ID");
      } catch (err) {
        setPaymentLoading(false);
        CriticalErrorHandler.reportPaymentError(err, { operation: "fetch_razorpay_key", order_id: orderId, user_id: userId });
        setErrorModal({ show: true, message: "Payment configuration error. Please contact support.", title: "Configuration Error" });
        return;
      }

      setLoading(true);
      setLoadingMessage("Preparing payment...");

      const browserDetection = detectInAppBrowser();
      if (browserDetection.isInAppBrowser) {
        const warningMessage = getBrowserCompatibilityMessage();
        if (warningMessage) setBrowserWarning(warningMessage);
      }

      // Cashfree has no hosted modal (pure custom checkout) — always routes through /checkout
      // regardless of checkoutMode. Razorpay-standard opens its own modal below, unchanged.
      if (checkoutMode === "custom" || order.gateway === "cashfree") {
        setLoading(false);
        setPaymentLoading(false);
        const checkoutState = {
          orderId, userId, amount: amountToBePaid, leadId,
          customerName, customerContact,
          customerEmail: `${customerContact}@astroshubh.com`,
          order,
          productName: product.service_name || "AstroShubh Service",
          productId: product.id,
          notes: { astroshubh_order_id: orderId, leadId, service_name: product.service_name || "AstroShubh Service", user_id: userId },
        };
        saveCheckoutSession(checkoutState);
        navigate("/checkout", { state: checkoutState });
        return;
      }

      setTimeout(async () => {
        setLoading(false);
        try {
          await openRazorpayPayment({
            razorpayKey,
            order,
            customerName,
            customerContact,
            notes: { astroshubh_order_id: orderId, leadId, service_name: product.service_name || "AstroShubh Service", user_id: userId, browser_info: browserDetection.browserName || "standard" },
            onSuccess: (response) => {
              setPaymentLoading(true);
              CriticalErrorHandler.reportPaymentSuccess({
                razorpay_payment_id: response.razorpay_payment_id, razorpay_order_id: response.razorpay_order_id,
                astroshubh_order_id: orderId, user_id: userId, amount, product_id: product?.id,
                service_name: product?.service_name || "AstroShubh Service", operation: "payment_gateway_success",
                browser_info: browserDetection.browserName || "standard",
              });

              let retryCount = 0;
              let backoffDelay = 2000;
              const maxRetries = 15;
              const maxBackoffDelay = 10000;

              const pollOrderStatus = async () => {
                try {
                  const orderCheck = await api.get(`/orders/order/${orderId}`);
                  const orderStatus = orderCheck.data?.data?.status;

                  if (orderStatus === "success" || orderStatus === "failed" || retryCount % 3 === 0) {
                    CriticalErrorHandler.reportPaymentStatusCheck("Order status verification", { order_id: orderId, status: orderStatus, retry_count: retryCount, backoff_delay: backoffDelay, operation: "order_status_polling" });
                  }

                  if (orderStatus === "success") {
                    if (paymentIntervalRef.current) { clearInterval(paymentIntervalRef.current); paymentIntervalRef.current = null; }
                    if (paymentTimeoutRef.current) { clearTimeout(paymentTimeoutRef.current); paymentTimeoutRef.current = null; }
                    setPaymentLoading(false);
                    setRechargeResState(null);
                    const formattedOrderId = String(orderId).padStart(6, "0");
                    CriticalErrorHandler.reportPaymentStatusCheck("Order processing completed successfully", { order_id: orderId, formatted_order_id: formattedOrderId, operation: "order_completion_success" });
                    setBrowserWarning(null);
                    navigate(`/order-success/ORD_${formattedOrderId}`);
                    return;
                  }

                  if (orderStatus === "failed") {
                    if (paymentIntervalRef.current) { clearInterval(paymentIntervalRef.current); paymentIntervalRef.current = null; }
                    if (paymentTimeoutRef.current) { clearTimeout(paymentTimeoutRef.current); paymentTimeoutRef.current = null; }
                    setPaymentLoading(false);
                    setRechargeResState(null);
                    CriticalErrorHandler.reportPaymentError(new Error("Order processing failed"), { order_id: orderId, order_status: orderStatus });
                    // Previously a dead end (no retry) — this branch now fires more often since
                    // the payment.failed/PAYMENT_FAILED_WEBHOOK backend fix correctly flips
                    // orders.status to 'failed' instead of leaving it stuck 'pending' forever.
                    // onRetry mirrors the synchronous Razorpay onFailure handler below.
                    setErrorModal({
                      show: true, message: "Order processing failed. Please contact support.", title: "Payment Failed",
                      onRetry: () => {
                        setErrorModal({ show: false, message: "", title: "" });
                        setTimeout(() => { handlePayment(); }, 300);
                      },
                    });
                    return;
                  }

                  if (orderStatus === "pending") {
                    retryCount = 0;
                    backoffDelay = 2000;
                    paymentTimeoutRef.current = setTimeout(pollOrderStatus, backoffDelay);
                    return;
                  }

                  // Unknown status - treat as pending but log warning
                  CriticalErrorHandler.reportPaymentStatusCheck("Unknown order status encountered", { order_id: orderId, unknown_status: orderStatus, retry_count: retryCount, operation: "order_status_unknown" });
                  retryCount = 0;
                  paymentTimeoutRef.current = setTimeout(pollOrderStatus, backoffDelay);
                } catch (err) {
                  CriticalErrorHandler.reportPaymentError(err, { operation: "order_status_check_failed", order_id: orderId, retry_count: retryCount, max_retries: maxRetries, error_details: err.message });
                  retryCount++;

                  if (retryCount >= maxRetries) {
                    if (paymentIntervalRef.current) { clearInterval(paymentIntervalRef.current); paymentIntervalRef.current = null; }
                    if (paymentTimeoutRef.current) { clearTimeout(paymentTimeoutRef.current); paymentTimeoutRef.current = null; }
                    setPaymentLoading(false);
                    setRechargeResState(null);
                    CriticalErrorHandler.reportPaymentError(new Error("Maximum retry attempts exceeded for order verification"), { order_id: orderId, error_details: err.message, retry_count: retryCount, max_retries: maxRetries });
                    setErrorModal({ show: true, message: "Unable to verify payment status after multiple attempts. Please check your orders or contact support.", title: "Verification Failed" });
                    return;
                  }

                  backoffDelay = Math.min(backoffDelay * 1.5, maxBackoffDelay);
                  CriticalErrorHandler.reportPaymentStatusCheck("Retrying order status check with exponential backoff", { order_id: orderId, retry_count: retryCount, max_retries: maxRetries, backoff_delay: backoffDelay, operation: "order_status_retry_with_backoff" });
                  paymentTimeoutRef.current = setTimeout(pollOrderStatus, backoffDelay);
                }
              };

              pollOrderStatus();

              // Safety timeout - stop polling after 2 minutes regardless of retries
              setTimeout(() => {
                if (paymentIntervalRef.current) { clearInterval(paymentIntervalRef.current); paymentIntervalRef.current = null; }
                if (paymentTimeoutRef.current) { clearTimeout(paymentTimeoutRef.current); paymentTimeoutRef.current = null; }
                setPaymentLoading((current) => {
                  if (current) {
                    setRechargeResState(null);
                    CriticalErrorHandler.reportPaymentError(new Error("Order verification timeout - safety limit reached"), { operation: "payment_verification_safety_timeout", order_id: orderId, user_id: userId, timeout_duration: "120000ms", retry_count: retryCount });
                    setErrorModal({ show: true, message: "Payment verification is taking longer than expected. Please check your orders or contact support.", title: "Timeout" });
                  }
                  return false;
                });
              }, 120000);
            },
            onFailure: (response) => {
              CriticalErrorHandler.reportPaymentError(new Error("Razorpay payment failed"), {
                operation: "razorpay_payment_failed", order_id: orderId, user_id: userId,
                error_code: response.error?.code, error_description: response.error?.description,
                error_reason: response.error?.reason, error_step: response.error?.step, error_source: response.error?.source,
                browser_info: browserDetection.browserName || "standard",
              });

              setPaymentLoading(false);
              if (paymentIntervalRef.current) { clearInterval(paymentIntervalRef.current); paymentIntervalRef.current = null; }
              if (paymentTimeoutRef.current) { clearTimeout(paymentTimeoutRef.current); paymentTimeoutRef.current = null; }

              let errorMessage = response.error?.description || "Payment processing failed.";
              let showFallback = false;
              if (response.error?.code && (
                response.error.code.includes("UPI") ||
                response.error.code.includes("GATEWAY_ERROR") ||
                response.error.description?.toLowerCase().includes("upi") ||
                response.error.description?.toLowerCase().includes("app")
              )) {
                showFallback = true;
                errorMessage = "UPI payment failed. This often happens in in-app browsers.";
              }

              const fallbackInstructions = generateFallbackPaymentInstructions(amountToBePaid, orderId);

              if (showFallback || browserDetection.isInAppBrowser) {
                let triggerReason = "unknown";
                if (response.error?.code?.includes("UPI")) triggerReason = "upi_failure";
                else if (response.error?.code?.includes("GATEWAY_ERROR")) triggerReason = "gateway_error";
                else if (browserDetection.isInAppBrowser) triggerReason = "in_app_browser";

                setFallbackModal({
                  show: true, amount: amountToBePaid, orderId,
                  instructions: fallbackInstructions, triggerReason,
                  errorInfo: { code: response.error?.code, message: response.error?.description, razorpayCode: response.error?.code },
                });
              } else {
                setErrorModal({
                  show: true, message: errorMessage, title: "Payment Processing Failed",
                  onRetry: () => {
                    setErrorModal({ show: false, message: "", title: "" });
                    setTimeout(() => { handlePayment(); }, 300);
                  },
                });
              }
            },
          });

          CriticalErrorHandler.reportPaymentGateway("Enhanced Razorpay payment gateway initialized successfully", {
            order_id: orderId, user_id: userId, razorpay_order_id: order.id, amount: order.amount,
            operation: "enhanced_razorpay_gateway_opened", browser_info: browserDetection.browserName || "standard",
          });
        } catch (razorpayError) {
          setPaymentLoading(false);
          CriticalErrorHandler.reportPaymentError(razorpayError, {
            operation: "enhanced_razorpay_init_failed", order_id: orderId, user_id: userId,
            razorpay_order_id: order.id, error_details: razorpayError.message, browser_info: browserDetection.browserName || "standard",
          });
          const fallbackInstructions = generateFallbackPaymentInstructions(amountToBePaid, orderId);
          setFallbackModal({
            show: true, amount: amountToBePaid, orderId, instructions: fallbackInstructions,
            triggerReason: "init_failure", errorInfo: { code: "RAZORPAY_INIT_FAILED", message: razorpayError.message },
          });
        }
      }, 400);
    } else {
      // No payment needed (wallet fully covers it) — OTP verification instead of a gateway
      let otpError = null;
      let infoMessage = "";
      try {
        await sendOTP(customerContact);
      } catch (err) {
        otpError = err;
      }

      const errorMessage = otpError?.response?.data?.error || otpError?.message || "";
      const isRecentlySentError = errorMessage && ["OTP recently sent", "recently sent", "cooldown", "wait"].some((s) => errorMessage.includes(s));

      if (!otpError || isRecentlySentError) {
        if (isRecentlySentError) infoMessage = "OTP already sent, please check your messages.";
        setOtpModal({ show: true, orderId, userId, leadId, infoMessage });
        startResendTimer();
        setPaymentLoading(false);
      } else {
        setPaymentLoading(false);
        CriticalErrorHandler.reportAuthError(otpError, { operation: "otp_send_wallet_payment", mobile: customerContact, order_id: orderId, user_id: userId, error_message: errorMessage });
        setErrorModal({ show: true, message: `Failed to send OTP: ${errorMessage || "Please try again."}`, title: "OTP Error" });
      }
    }
  };

  React.useEffect(() => {
    if (!orderId) {
      // Arrived here without valid route state (e.g. direct URL access / back-button after
      // completion) — nothing to pay for, send back home rather than crash on undefined.
      navigate("/", { replace: true });
      return;
    }
    handlePayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    return () => {
      if (paymentIntervalRef.current) clearInterval(paymentIntervalRef.current);
      if (paymentTimeoutRef.current) clearTimeout(paymentTimeoutRef.current);
      criticalLogger.flushNow();
    };
  }, []);

  return (
    <div style={{ minHeight: "100vh" }}>
      {(loading || paymentLoading) && <SpinnerOverlay message={loadingMessage} />}
      {browserWarning && (
        <div style={{ padding: "12px 16px", background: "#fff3e0", color: "#e65100", textAlign: "center", fontSize: 14 }}>
          {browserWarning}
        </div>
      )}
      <OTPModal
        show={otpModal.show}
        onClose={handleOTPClose}
        onVerify={handleOTPVerify}
        mobile={customerContact}
        onResend={handleOTPResend}
        resendDisabled={resendDisabled}
        resendTimer={resendTimer}
        infoMessage={otpModal.infoMessage}
      />
      <ErrorModal
        show={errorModal.show}
        message={errorModal.message}
        title={errorModal.title}
        onRetry={errorModal.onRetry}
        onClose={() => setErrorModal({ show: false, message: "", title: "" })}
      />
      <FallbackPaymentModal
        show={fallbackModal.show && checkoutMode !== "custom"}
        amount={fallbackModal.amount}
        orderId={fallbackModal.orderId}
        fallbackInstructions={fallbackModal.instructions}
        triggerReason={fallbackModal.triggerReason}
        errorInfo={fallbackModal.errorInfo}
        onClose={() => setFallbackModal({ show: false, amount: 0, orderId: null, instructions: null, triggerReason: null, errorInfo: null })}
        onRetry={() => {
          setFallbackModal({ show: false, amount: 0, orderId: null, instructions: null, triggerReason: null, errorInfo: null });
          setTimeout(() => { handlePayment(); }, 300);
        }}
        onSuccess={(paymentData) => {
          setFallbackModal({ show: false, amount: 0, orderId: null, instructions: null, triggerReason: null, errorInfo: null });
          navigate(`/order-success?orderId=${paymentData.orderId}&paymentMethod=qr`);
        }}
      />
    </div>
  );
}
